import { localizedAccountDisplayName, uniqueDisplayAccount } from "../../utils/localizedAccountDisplayName";
// src/pages/deposits/DepositDetail.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { ParseKeys } from "i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";

import { Button } from "../../components/Button";
import { AppleComboBox } from "../../components/AppleComboBox";
import { apiDelete, apiGet, apiPost } from "../../api/client";
import { companyRoute } from "../../utils/routeHelpers";
import { PurchaseInfoHeader } from "../../components/PurchaseInfoHeader";
import { ymdTodayLocal, formatAnyDateToLocale } from "../../utils/dateUtils";
import { formatNumber, formatMoney } from "../../utils/formatNumber";
import { A } from "../../styles/appleClasses";
import { AppleDateInput } from "../../components/AppleDateInput";
import MemoSection from "../../components/MemoSection";
import { AppleNumberInput } from "../../components/AppleNumberInput";
import {
  isClosedPeriodError,
  isDateClosed,
} from "../../utils/closedPeriod";
import {
  canonicalDepositWriteoff,
  fingerprintDepositWriteoff,
  decideDepositWriteoffAttempt,
  resolveDepositWriteoffSuccess,
  resolveDepositWriteoffDefiniteRejection,
  resolveDepositWriteoffAmbiguousFailure,
  classifyDepositWriteoffFailure,
  randomUuidKey,
  shouldRestoreAmbiguousAttempt,
  IDLE_DEPOSIT_WRITEOFF_ATTEMPT,
  type DepositWriteoffAttempt,
} from "../../utils/depositWriteoffIdempotency";
import {
  canonicalDepositRefund,
  fingerprintDepositRefund,
  decideDepositRefundAttempt,
  resolveDepositRefundSuccess,
  resolveDepositRefundDefiniteRejection,
  resolveDepositRefundAmbiguousFailure,
  classifyDepositRefundFailure,
  randomUuidKey as randomUuidKeyForRefund,
  shouldRestoreAmbiguousAttempt as shouldRestoreAmbiguousRefundAttempt,
  IDLE_DEPOSIT_REFUND_ATTEMPT,
  type DepositRefundAttempt,
} from "../../utils/depositRefundIdempotency";

type Account = {
  id: number | string;
  name: string;
  type?: string | null;
  kind?: string | null;
  is_system?: boolean | null;
};

type DepositItem = {
  id: number;
  date: string; // YYYY-MM-DD
  memo?: string | null;

  direction: "outgoing" | "incoming";
  party_name?: string | null;

  bank_account_id: number;
  bank_name?: string | null;

  original_amount: number;
  remaining_amount: number;
  status: "open" | "closed" | "voided";
};

type DepositEvent = {
  id: number;
  deposit_id: number;
  kind: string; // "apply" | "refund" | "writeoff"
  date: string; // YYYY-MM-DD (or ISO)
  amount: number;

  bank_account_id?: number | null; // refund
  expense_account_id?: number | null; // writeoff
  doc_type?: string | null; // apply
  doc_id?: number | null; // apply
  journal_entry_id?: number | null;
};

const lower = (s?: string | null) => (s || "").toLowerCase().trim();
const accountDisplayName = (a?: Account | null) =>
  lower(a?.kind) === "credit_card_payable" ? "Credit Card" : a?.name || "";

function money(n: number | undefined | null) {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  return formatMoney(n);
}

type LocalMessage = { key: ParseKeys; date?: string | null };
type DisplayMessage = string | LocalMessage;
const localMessage = (key: ParseKeys, date?: string | null): LocalMessage => ({ key, date });
class DepositNotFoundError extends Error {}
const errorMessage = (e: any): DisplayMessage | undefined => e instanceof DepositNotFoundError
  ? localMessage("depositDetail.notFound") : e?.message;

function niceDetail(d: any): string {
  if (!d) return "";
  if (Array.isArray(d)) {
    return d.map((x) => `${(x.loc || []).join(".")}: ${x.msg}`).join(" | ");
  }
  return String(d);
}

// Backend has GET /deposits (list). If no GET /deposits/{id}, we find it from the list.
async function fetchDeposit(
  company_id: string,
  deposit_id: string,
): Promise<DepositItem> {
  const list = await apiGet<DepositItem[]>(
    `/deposits?company_id=${company_id}`,
  );
  const found = (list || []).find((x) => String(x.id) === String(deposit_id));
  if (!found) throw new DepositNotFoundError("Deposit not found");
  return found;
}

async function fetchDepositEventsBestEffort(
  deposit_id: string,
): Promise<DepositEvent[]> {
  // Primary endpoint you should add on backend:
  // GET /deposits/{deposit_id}/events
  return await apiGet<DepositEvent[]>(`/deposits/${deposit_id}/events`);
}

type DisplayStatus = "open" | "partial" | "closed" | "voided";
function displayStatusFor(d?: DepositItem | null): DisplayStatus {
  if (!d) return "open";
  const st = lower(d.status || "open");
  const orig = Number(d.original_amount || 0);
  const rem = Number(d.remaining_amount || 0);

  if (st === "voided") return "voided";
  if (rem <= 0 || st === "closed") return "closed";
  if (orig > 0 && rem > 0 && rem < orig) return "partial";
  return "open";
}

function eventLabel(kind: string, t: (key: ParseKeys) => string) {
  const k = lower(kind);
  if (k === "apply") return t("depositDetail.historicalApply");
  if (k === "refund") return t("depositDetail.refund");
  if (k === "writeoff") return t("depositDetail.transferredToExpense");
  return kind || "—";
}

function applyTargetLabel(t: (key: "depositDetail.billReference" | "depositDetail.invoiceReference", options: { id: number }) => string, doc_type?: string | null, doc_id?: number | null) {
  const docType = lower(doc_type || "");
  if (!docType || !doc_id) return "—";
  if (docType === "bill" || docType === "purchase") return t("depositDetail.billReference", { id: doc_id });
  if (docType === "invoice" || docType === "sale") return t("depositDetail.invoiceReference", { id: doc_id });
  return `${doc_type} #${doc_id}`;
}

// Apple small button override (your Button.tsx defaults are larger)
const btnSmBase =
  "h-10 px-4 py-0 rounded-xl text-[13px] font-medium shadow-sm " +
  "inline-flex items-center justify-center leading-none";
const btnSmPrimary = `${btnSmBase} min-w-[120px]`;
const btnSmSecondary = `${btnSmBase} min-w-[120px] bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-200`;
const btnSmDanger = `${btnSmBase} min-w-[120px] bg-red-600 hover:bg-red-700 active:bg-red-800 border border-red-600 focus:ring-red-200`;

export default function DepositDetail() {
  const { t } = useTranslation();
  const renderMessage = (message: DisplayMessage) => typeof message === "string"
    ? message
    : message.key === "sales.errors.closedSubmit"
      ? t("sales.errors.closedSubmit", { date: message.date
          ? formatAnyDateToLocale(message.date.slice(0, 10)) : t("sales.errors.closedDate") })
      : t(message.key);
  const displayAccount = useCallback((a?: Account | null) => lower(a?.kind) === "credit_card_payable"
    ? t("expenses.entry.creditCard") : a?.name || "", [t]);
  const navigate = useNavigate();
  const { company_id = "", deposit_id = "" } = useParams<{
    company_id: string;
    deposit_id: string;
  }>();

  const goHome = () => navigate(companyRoute(company_id!, "/"));
  const goBack = () =>
    navigate(companyRoute(company_id!, "/recognition/deposits"));

  // appleClasses (safe fallbacks)
  const bodyCls = (A as any).body || "text-[13px] font-normal text-gray-700";
  const cardCls =
    (A as any).card || "bg-white border border-gray-200 rounded-2xl shadow-sm";
  const inputCls =
    (A as any).input ||
    "w-full h-10 rounded-xl px-3 border border-gray-200 bg-white text-[13px] text-gray-700 " +
      "focus:outline-none focus:ring-2 focus:ring-appleblueD focus:border-appleblue transition";
  const tableCls =
    (A as any).table || "w-full table-fixed text-[13px] text-gray-700";

  const [deposit, setDeposit] = useState<DepositItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<DisplayMessage | null>(null);
  const [closedThroughDate, setClosedThroughDate] = useState<string | null>(
    null,
  );
  const [accounts, setAccounts] = useState<Account[]>([]);

  const accountNameById = useMemo(() => {
    const m = new Map<number, Account>();
    (accounts || []).forEach((a) => {
      const idNum = Number(a.id);
      if (!Number.isNaN(idNum)) m.set(idNum, a);
    });
    return m;
  }, [accounts]);

  const expenseAccounts = useMemo(() => {
    return (accounts || [])
      .filter((a) => lower(a.type) === "expense" && !a.is_system)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [accounts]);

  const bankAccounts = useMemo(() => {
    const allowed = new Set(["cash", "bank"]);
    const excluded = new Set(["cash_system", "bank_system"]);
    return (accounts || [])
      .filter((a) => {
        const type = lower(a.type);
        const kind = lower(a.kind);
        if (type === "liability" && kind === "credit_card_payable")
          return true;
        if (type !== "asset") return false;
        if (a.is_system) return false;
        if (excluded.has(kind)) return false;
        return allowed.has(kind);
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [accounts]);

  // History
  const [events, setEvents] = useState<DepositEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<DisplayMessage | null>(null);
  const [eventsLoadedOnce, setEventsLoadedOnce] = useState(false);

  // Memo (standard)
  const [editingMemo, setEditingMemo] = useState(false);
  const [memoDraft, setMemoDraft] = useState("");
  const [memoSaving, setMemoSaving] = useState(false);
  const [memoError, setMemoError] = useState<DisplayMessage | null>(null);

  // Toggle between actions
  const [actionTab, setActionTab] = useState<"apply" | "refund">("apply");

  // Date-rejection guards
  const [backendRejectsApplyDate, setBackendRejectsApplyDate] = useState(false);
  const [backendRejectsRefundDate, setBackendRejectsRefundDate] =
    useState(false);

  // Apply (writeoff)
  const [applyAcct, setApplyAcct] = useState<Account | null>(null);
  const [applyAmt, setApplyAmt] = useState("");
  const [applyDate, setApplyDate] = useState<string>(() => ymdTodayLocal());
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<DisplayMessage | null>(null);

  // Deposit write-off idempotency attempt lifecycle (see
  // utils/depositWriteoffIdempotency.ts). A ref holding a Map, not state:
  // must be stable across renders, must not itself trigger a re-render,
  // and must be readable synchronously by the in-flight guard below.
  //
  // ONE INDEPENDENT SLOT PER deposit_id -- NOT one shared attempt for the
  // whole component. This component is mounted once for its route and
  // reused across different deposit_id route-param values without
  // remounting, so an ambiguous attempt on Deposit A must be retained
  // under A's own key while Deposit B's key (absent from the map, or
  // resolved back to idle) starts fresh and is immediately usable --
  // never blocked by A's unresolved state. A deposit's entry is removed
  // entirely once its attempt resolves to idle (success or definite
  // rejection), so the map only ever holds in-flight/ambiguous attempts.
  // Independent of any Refund attempt state -- Apply and Refund are
  // different operations and must never block each other.
  const depositWriteoffAttemptsRef = useRef<Map<number, DepositWriteoffAttempt>>(
    new Map(),
  );
  const getDepositWriteoffAttempt = (depositId: number): DepositWriteoffAttempt =>
    depositWriteoffAttemptsRef.current.get(depositId) ?? IDLE_DEPOSIT_WRITEOFF_ATTEMPT;
  // Purely synchronous double-click/repeat-tap guard for whichever
  // deposit's submit button was just clicked -- independent of the
  // per-deposit attempt map above. Mirrors FixedAssetDetail.tsx's
  // fixedAssetPaymentInFlightRef. A single boolean is correct here (not a
  // per-deposit map): only one doApply invocation can ever be executing at
  // a time from user interaction, since only one deposit's form is
  // rendered/clickable at once.
  const depositWriteoffInFlightRef = useRef(false);

  // Refund
  const [refundBank, setRefundBank] = useState<Account | null>(null);
  const [refundAmt, setRefundAmt] = useState("");
  const [refundDate, setRefundDate] = useState<string>(() => ymdTodayLocal());
  const [refunding, setRefunding] = useState(false);
  const [refundError, setRefundError] = useState<DisplayMessage | null>(null);

  // Deposit refund idempotency attempt lifecycle (see
  // utils/depositRefundIdempotency.ts). A ref holding a Map, not state:
  // must be stable across renders, must not itself trigger a re-render,
  // and must be readable synchronously by the in-flight guard below.
  //
  // ONE INDEPENDENT SLOT PER deposit_id -- NOT one shared attempt for the
  // whole component, and a COMPLETELY SEPARATE Map from
  // depositWriteoffAttemptsRef above: Refund and Write-off are different
  // operations and must never share a key, attempt, fingerprint,
  // submitted payload, in-flight guard, or ambiguous state, even for the
  // SAME deposit_id. This component is mounted once for its route and
  // reused across different deposit_id route-param values without
  // remounting, so an ambiguous refund attempt on Deposit A must be
  // retained under A's own key while Deposit B's key (absent from the map,
  // or resolved back to idle) starts fresh and is immediately usable --
  // never blocked by A's unresolved state. A deposit's entry is removed
  // entirely once its refund attempt resolves to idle (success or
  // definite rejection), so the map only ever holds in-flight/ambiguous
  // attempts.
  const depositRefundAttemptsRef = useRef<Map<number, DepositRefundAttempt>>(
    new Map(),
  );
  const getDepositRefundAttempt = (depositId: number): DepositRefundAttempt =>
    depositRefundAttemptsRef.current.get(depositId) ?? IDLE_DEPOSIT_REFUND_ATTEMPT;
  // Purely synchronous double-click/repeat-tap guard for whichever
  // deposit's refund submit button was just clicked -- independent of the
  // per-deposit attempt map above, and independent of
  // depositWriteoffInFlightRef. Mirrors depositWriteoffInFlightRef's own
  // identical rationale.
  const depositRefundInFlightRef = useRef(false);

  // Delete (modal)
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<DisplayMessage | null>(null);

  const identityLabel = deposit?.direction === "outgoing" ? t("directory.vendors.singular")
    : deposit?.direction === "incoming" ? t("depositDetail.party") : "Party";
  const headerLabels = (key: ParseKeys): string => {
    switch (key) {
      case "purchases.header.supplier": return deposit?.direction === "outgoing" || deposit?.direction === "incoming" ? identityLabel : "Supplier";
      case "sales.common.outstanding": return t("spendingDeposits.remaining");
      case "sales.common.total": return t("spendingDeposits.original");
      case "purchases.status.paid": return t("spendingDeposits.statusClosed");
      case "purchases.status.partial": return t("spendingDeposits.statusPartial");
      case "purchases.status.voided": return t("sales.list.voided");
      default: return t(key);
    }
  };

  const original = Number(deposit?.original_amount || 0);
  const remaining = Number(deposit?.remaining_amount || 0);
  const usedAmount = useMemo(() => {
    return (events || [])
      .filter((ev) => {
        const k = lower(ev.kind);
        return k === "apply" || k === "writeoff";
      })
      .reduce((sum, ev) => sum + Number(ev.amount || 0), 0);
  }, [events]);

  const refundedAmount = useMemo(() => {
    return (events || [])
      .filter((ev) => lower(ev.kind) === "refund")
      .reduce((sum, ev) => sum + Number(ev.amount || 0), 0);
  }, [events]);
  const ds = displayStatusFor(deposit);
  const isVoided = ds === "voided";
  const isClosed = ds === "closed";
  const canAct = !!deposit && !isClosed && !isVoided && remaining > 0;

  const statusBubbles = useMemo(() => {
    if (!deposit) return [];
    const s = displayStatusFor(deposit);
    if (s === "voided")
      return [{ label: t("sales.list.voided"), bubbleType: "voided" as const }];
    if (s === "partial")
      return [{ label: t("spendingDeposits.statusPartial"), bubbleType: "partial" as const }];
    if (s === "closed")
      return [{ label: t("spendingDeposits.statusClosed"), bubbleType: "paid" as const }];
    return [{ label: t("spendingDeposits.statusOpen"), bubbleType: "open" as any }];
  }, [deposit, t]);

  const lines = useMemo(() => {
    if (!deposit) return [];
    return [
      {
        id: deposit.id,
        party: deposit.party_name || "—",
        account:
          displayAccount(accountNameById.get(Number(deposit.bank_account_id))) ||
          deposit.bank_name ||
          "—",
        amount: original || 0,
      },
    ];
  }, [accountNameById, deposit, original, displayAccount]);

  const historyRows = useMemo(() => {
    const xs = (events || []).slice();
    xs.sort((a, b) => {
      const da = String(a.date || "");
      const db = String(b.date || "");
      if (da < db) return -1;
      if (da > db) return 1;
      return (a.id || 0) - (b.id || 0);
    });

    return xs.map((ev) => {
      const k = lower(ev.kind);
      const dateStr = String(ev.date || "").slice(0, 10);
      const date = dateStr ? formatAnyDateToLocale(dateStr) : "—";

      let accountOrTarget = "—";
      if (k === "refund") {
        const id = ev.bank_account_id;
        accountOrTarget =
          id != null ? displayAccount(accountNameById.get(Number(id))) || "—" : "—";
      } else if (k === "writeoff") {
        const id = ev.expense_account_id;
        accountOrTarget =
          id != null ? displayAccount(accountNameById.get(Number(id))) || "—" : "—";
      } else if (k === "apply") {
        accountOrTarget = applyTargetLabel(t, ev.doc_type, ev.doc_id);
      }

      const displayAccountOrTarget = k === "writeoff"
        ? localizedAccountDisplayName(uniqueDisplayAccount(accounts, ev.expense_account_id), t, accountOrTarget) : accountOrTarget;
      return {
        id: ev.id,
        date,
        displayAccountOrTarget,
        action: eventLabel(ev.kind, t),
        accountOrTarget,
        amount: Number(ev.amount || 0),
      };
    });
  }, [events, accounts, accountNameById, displayAccount, t]);

  const showHistorySection =
    eventsLoading ||
    historyRows.length > 0 ||
    (eventsError && (typeof eventsError !== "string" || eventsError.trim().length > 0));

  // Delete only when untouched + no history (once loaded)
  const deleteAvailable =
    !!deposit &&
    !isVoided &&
    !isClosed &&
    Math.abs(original - remaining) < 0.000001 &&
    !(eventsLoadedOnce && events.length > 0);

  useEffect(() => {
    if (!company_id || !deposit_id) return;
    setLoading(true);
    setErr(null);

    fetchDeposit(company_id, deposit_id)
      .then((d) => setDeposit(d || null))
      .catch((e: any) =>
        setErr(
          e instanceof DepositNotFoundError ? localMessage("depositDetail.notFound") :
          niceDetail(e?.response?.data?.detail) ||
            errorMessage(e) ||
            localMessage("depositDetail.loadError"),
        ),
      )
      .finally(() => setLoading(false));
  }, [company_id, deposit_id]);

  useEffect(() => {
    if (!company_id) return;
    apiGet<Account[]>(`/accounts?company_id=${company_id}`)
      .then((xs) => setAccounts(xs || []))
      .catch(() => setAccounts([]));
  }, [company_id]);

  useEffect(() => {
    if (!company_id) return;

    apiGet<{
      closed_through_date?: string | null;
    }>(`/settings/financial-year-end?company_id=${company_id}`)
      .then((res) => {
        setClosedThroughDate(res?.closed_through_date || null);
      })
      .catch(() => {
        setClosedThroughDate(null);
      });
  }, [company_id]);

  // Load events (history)
  useEffect(() => {
    if (!deposit_id) return;

    let cancelled = false;
    setEventsLoading(true);
    setEventsError(null);

    fetchDepositEventsBestEffort(deposit_id)
      .then((xs) => {
        if (cancelled) return;
        setEvents(xs || []);
        setEventsLoadedOnce(true);
      })
      .catch((e: any) => {
        if (cancelled) return;

        // If endpoint not added yet, do NOT show "Not Found" noise
        if (e?.response?.status === 404) {
          setEvents([]);
          setEventsLoadedOnce(true);
          return;
        }

        setEventsError(
          niceDetail(e?.response?.data?.detail) ||
            errorMessage(e) ||
            localMessage("depositDetail.historyError"),
        );
        setEventsLoadedOnce(true);
      })
      .finally(() => {
        if (cancelled) return;
        setEventsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [deposit_id]);

  // Initialize/restore the write-off form whenever the CURRENT deposit's
  // own attempt slot says to -- runs on deposit_id changes (route
  // navigation -- DepositDetail.tsx is mounted once for the route and
  // reused across different deposit_id values without remounting) and on
  // expenseAccounts finishing its own async load (so an account can
  // actually be matched/defaulted once options exist).
  //
  // - If THIS deposit has a live ambiguous attempt (see
  //   shouldRestoreAmbiguousAttempt's docstring): restore its exact
  //   submitted date/amount/expense account, never fresh defaults --
  //   otherwise the very next submission would compute a different
  //   fingerprint and get blocked as an "edited retry".
  // - Otherwise (idle -- no retained attempt for this deposit, whether
  //   because none was ever made or a prior one already resolved):
  //   initialize/reset the form to fresh defaults (today's date, no
  //   amount, first available expense account) rather than leaving a
  //   PREVIOUSLY VIEWED deposit's values on screen.
  useEffect(() => {
    if (deposit?.id == null) return;
    const depositIdNum = Number(deposit.id);
    const attempt = getDepositWriteoffAttempt(depositIdNum);

    if (shouldRestoreAmbiguousAttempt(attempt) && attempt.submittedPayload) {
      const payload = attempt.submittedPayload;
      setApplyDate(payload.date);
      setApplyAmt(payload.amount);
      const matchedAccount =
        expenseAccounts.find(
          (a) => String(a.id) === String(payload.expense_account_id),
        ) || null;
      if (matchedAccount) setApplyAcct(matchedAccount);
      return;
    }

    setApplyAmt("");
    setApplyDate(ymdTodayLocal());
    setApplyAcct(expenseAccounts[0] || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit?.id, expenseAccounts.length]);

  // Initialize/restore the refund form whenever the CURRENT deposit's own
  // refund attempt slot says to -- runs on deposit_id changes (route
  // navigation -- DepositDetail.tsx is mounted once for the route and
  // reused across different deposit_id values without remounting) and on
  // bankAccounts finishing its own async load (so an account can actually
  // be matched/defaulted once options exist). Mirrors the write-off
  // restoration effect above exactly, but reads/writes ONLY
  // depositRefundAttemptsRef -- entirely independent of the write-off
  // attempt for this same deposit.
  //
  // - If THIS deposit has a live ambiguous REFUND attempt (see
  //   shouldRestoreAmbiguousRefundAttempt's docstring): restore its exact
  //   submitted date/amount/bank account, never fresh defaults --
  //   otherwise the very next submission would compute a different
  //   fingerprint and get blocked as an "edited retry". Never overwrites
  //   the retained attempt itself, only the form fields mirroring it.
  // - Otherwise (idle -- no retained refund attempt for this deposit,
  //   whether because none was ever made or a prior one already
  //   resolved): initialize/reset the form to fresh defaults (today's
  //   date, no amount, the deposit's own bank account or the first
  //   available one) rather than leaving a PREVIOUSLY VIEWED deposit's
  //   refund values on screen. This reset branch runs only when there is
  //   no ambiguous attempt to restore, so it can never run after and
  //   overwrite a valid ambiguous restoration.
  useEffect(() => {
    if (deposit?.id == null) return;
    const depositIdNum = Number(deposit.id);
    const attempt = getDepositRefundAttempt(depositIdNum);

    if (shouldRestoreAmbiguousRefundAttempt(attempt) && attempt.submittedPayload) {
      const payload = attempt.submittedPayload;
      setRefundDate(payload.date);
      setRefundAmt(payload.amount);
      const matchedAccount =
        bankAccounts.find(
          (b) => String(b.id) === String(payload.bank_account_id),
        ) || null;
      if (matchedAccount) setRefundBank(matchedAccount);
      return;
    }

    setRefundAmt("");
    setRefundDate(ymdTodayLocal());
    const preferred =
      bankAccounts.find(
        (b) => String(b.id) === String(deposit?.bank_account_id),
      ) ||
      bankAccounts[0] ||
      null;
    setRefundBank(preferred);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit?.id, bankAccounts.length]);

  useEffect(() => {
    setEditingMemo(false);
    setMemoError(null);
    setMemoDraft(deposit?.memo || "");
  }, [deposit?.id]);

  // Close delete modal on Escape
  useEffect(() => {
    if (!showDelete) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !deleting) {
        setShowDelete(false);
        setDeleteError(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showDelete, deleting]);

  const refresh = async () => {
    if (!company_id || !deposit_id) return;
    const d = await fetchDeposit(company_id, deposit_id);
    setDeposit(d);

    try {
      const xs = await fetchDepositEventsBestEffort(deposit_id);
      setEvents(xs || []);
      setEventsLoadedOnce(true);
    } catch {
      // ignore
    }
  };

  const isClosedApplyDate = useMemo(() => {
    return isDateClosed(applyDate, closedThroughDate);
  }, [applyDate, closedThroughDate]);

  const isClosedRefundDate = useMemo(() => {
    return isDateClosed(refundDate, closedThroughDate);
  }, [refundDate, closedThroughDate]);

  const onMoneyBlur = (setter: (s: string) => void, v: string) => {
    setter(String(v || "").trim());
  };

  // Apply = writeoff
  const doApply = async () => {
    setApplyError(null);
    if (!deposit || !deposit_id) return;
    if (!applyAcct) return setApplyError(localMessage("depositDetail.selectExpenseError"));

    // Synchronous guard against two immediate invocations (e.g. a double
    // click or repeated tap before React re-renders the disabled button)
    // resulting in two POSTs -- `applying` state alone can't catch this
    // since it only updates on the next render. Independent of the
    // attempt state machine below. Mirrors FixedAssetDetail.tsx's
    // fixedAssetPaymentInFlightRef guard.
    if (depositWriteoffInFlightRef.current) return;

    const amt = Number(
      String(applyAmt || "")
        .replace(/,/g, "")
        .trim(),
    );
    if (!Number.isFinite(amt) || amt <= 0)
      return setApplyError(localMessage("newDeposit.positiveAmount"));
    if (amt > remaining) return setApplyError(localMessage("depositDetail.amountExceeds"));
    if (!backendRejectsApplyDate && !applyDate)
      return setApplyError(localMessage("depositDetail.selectDate"));

    const depositIdNum = Number(deposit.id);

    // Validation passed -- only now do we build the canonical payload and
    // ask the state machine whether THIS DEPOSIT'S OWN slot (never any
    // other deposit's) is a first attempt (mint a new key), an unchanged
    // retry of a still-ambiguous attempt (reuse its key), or an edited
    // retry of one (block, no network call). memo is always "" -- this
    // form has no memo input for write-off, matching the pre-existing
    // behavior of never sending one.
    const canonicalPayload = canonicalDepositWriteoff(
      depositIdNum,
      amt,
      applyAcct.id,
      applyDate,
      "",
    );
    const fingerprint = fingerprintDepositWriteoff(canonicalPayload);
    const decision = decideDepositWriteoffAttempt(
      getDepositWriteoffAttempt(depositIdNum),
      canonicalPayload,
      fingerprint,
      randomUuidKey,
    );

    if (decision.kind === "blocked-mismatch") {
      // The previous attempt under this key may already have been
      // recorded -- never silently mint a new key or retry with edited
      // details under the same one.
      setApplyError(localMessage("depositDetail.writeoffRetry"));
      return;
    }
    if (decision.kind === "blocked-in-flight") {
      // Unreachable in practice -- depositWriteoffInFlightRef.current
      // already returns above before this point. Kept for correctness of
      // the pure decision function itself.
      return;
    }

    // Store the new attempt ONLY under this deposit's own key -- every
    // other deposit's slot in the map is untouched.
    depositWriteoffAttemptsRef.current.set(depositIdNum, decision.attempt);
    depositWriteoffInFlightRef.current = true;

    const basePayload: any = {
      expense_account_id: Number(applyAcct.id),
      amount: amt,
    };
    const payload: any = {
      ...basePayload,
      idempotency_key: decision.idempotencyKey,
    };
    if (!backendRejectsApplyDate) payload.date = applyDate;

    setApplying(true);
    try {
      await apiPost(`/deposits/${deposit_id}/writeoff`, payload);

      // Confirmed success (create or replay -- both return the same
      // response shape): clear ONLY this deposit's slot back to idle so
      // its next write-off (e.g. a later distinct partial write-off) gets
      // a fresh key instead of leaking this one into it. Every other
      // deposit's slot in the map is untouched.
      depositWriteoffAttemptsRef.current.set(depositIdNum, resolveDepositWriteoffSuccess());

      setApplyAmt("");
      setApplyDate(ymdTodayLocal());
      await refresh();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const msg =
        typeof detail === "string" ? detail : JSON.stringify(detail || "");

      const failureClass = classifyDepositWriteoffFailure(e, isClosedPeriodError);

      if (failureClass === "closed-period") {
        // Definite pre-commit rejection: assert_year_open raises before any
        // claim/business write commits, so nothing was persisted. Clear
        // this deposit's slot so a corrected date can retry under a new
        // key.
        depositWriteoffAttemptsRef.current.set(depositIdNum, resolveDepositWriteoffDefiniteRejection());
        setApplyError(localMessage("sales.errors.closedSubmit", closedThroughDate));
        return;
      }

      if (failureClass === "idempotency-conflict") {
        // Definite: this key is permanently bound to a different,
        // already-completed request. Retrying under it can never succeed
        // for this payload -- clear this deposit's slot so the next
        // attempt mints a fresh one.
        depositWriteoffAttemptsRef.current.set(depositIdNum, resolveDepositWriteoffDefiniteRejection());
        setApplyError(localMessage("depositDetail.writeoffConflict"));
        return;
      }

      if (failureClass === "definite-rejection") {
        depositWriteoffAttemptsRef.current.set(depositIdNum, resolveDepositWriteoffDefiniteRejection());

        // Pre-existing legacy fallback, unrelated to idempotency and left
        // untouched here: some historical backend version 422'd on an
        // explicit `date` field with "Input should be None".
        // schemas.DepositWriteoffCreate.date is now a REQUIRED field (see
        // the idempotency patch), so this fallback's own retry below
        // (which omits date entirely) would itself now 422 if ever
        // reached -- this branch was already unreachable in practice
        // before this patch (confirmed: the backend has long accepted an
        // explicit date fine) and remains so; not something this patch
        // removes or otherwise changes.
        if (
          e?.response?.status === 422 &&
          !backendRejectsApplyDate &&
          msg.includes("Input should be None")
        ) {
          setBackendRejectsApplyDate(true);
          try {
            await apiPost(`/deposits/${deposit_id}/writeoff`, basePayload);
            setApplyAmt("0.00");
            setApplyDate(ymdTodayLocal());
            await refresh();
            return;
          } catch (e2: any) {
            setApplyError(
              niceDetail(e2?.response?.data?.detail) ||
                errorMessage(e2) ||
                localMessage("depositDetail.writeoffError"),
            );
            return;
          }
        }

        setApplyError(
          niceDetail(detail) || errorMessage(e) || localMessage("depositDetail.writeoffError"),
        );
        return;
      }

      // Ambiguous (network failure, timeout, unreadable response, HTTP
      // 500, or anything else unrecognized): retain the key, fingerprint,
      // and submitted payload under THIS deposit's own slot so an
      // unchanged retry reuses the same key and safely replays instead of
      // risking a duplicate; an edited retry of THIS SAME deposit stays
      // blocked above. Every other deposit's own slot is completely
      // unaffected and remains immediately usable for its own fresh
      // attempt.
      depositWriteoffAttemptsRef.current.set(
        depositIdNum,
        resolveDepositWriteoffAmbiguousFailure(decision.attempt),
      );
      setApplyError(
        niceDetail(detail) || errorMessage(e) || localMessage("depositDetail.writeoffError"),
      );
    } finally {
      depositWriteoffInFlightRef.current = false;
      setApplying(false);
    }
  };

  const doRefund = async () => {
    setRefundError(null);
    if (!deposit || !deposit_id) return;
    if (!refundBank) return setRefundError(localMessage("depositDetail.selectRefundError"));

    // Synchronous guard against two immediate invocations (e.g. a double
    // click or repeated tap before React re-renders the disabled button)
    // resulting in two POSTs -- `refunding` state alone can't catch this
    // since it only updates on the next render. Independent of the
    // attempt state machine below, and independent of
    // depositWriteoffInFlightRef. Mirrors depositWriteoffInFlightRef's own
    // identical guard.
    if (depositRefundInFlightRef.current) return;

    const amt = Number(
      String(refundAmt || "")
        .replace(/,/g, "")
        .trim(),
    );
    if (!Number.isFinite(amt) || amt <= 0)
      return setRefundError(localMessage("newDeposit.positiveAmount"));
    if (amt > remaining) return setRefundError(localMessage("depositDetail.amountExceeds"));
    if (!backendRejectsRefundDate && !refundDate)
      return setRefundError(localMessage("depositDetail.selectDate"));

    const depositIdNum = Number(deposit.id);

    // Validation passed -- only now do we build the canonical payload and
    // ask the state machine whether THIS DEPOSIT'S OWN refund slot (never
    // any other deposit's, and never this deposit's write-off slot -- a
    // completely separate Map) is a first attempt (mint a new key), an
    // unchanged retry of a still-ambiguous attempt (reuse its key), or an
    // edited retry of one (block, no network call). memo is always "" --
    // this form has no memo input for refund, matching the pre-existing
    // behavior of never sending one.
    const canonicalPayload = canonicalDepositRefund(
      depositIdNum,
      amt,
      refundBank.id,
      refundDate,
      "",
    );
    const fingerprint = fingerprintDepositRefund(canonicalPayload);
    const decision = decideDepositRefundAttempt(
      getDepositRefundAttempt(depositIdNum),
      canonicalPayload,
      fingerprint,
      randomUuidKeyForRefund,
    );

    if (decision.kind === "blocked-mismatch") {
      // The previous attempt under this key may already have been
      // recorded -- never silently mint a new key or retry with edited
      // details under the same one.
      setRefundError(localMessage("depositDetail.refundRetry"));
      return;
    }
    if (decision.kind === "blocked-in-flight") {
      // Unreachable in practice -- depositRefundInFlightRef.current
      // already returns above before this point. Kept for correctness of
      // the pure decision function itself.
      return;
    }

    // Store the new attempt ONLY under this deposit's own refund key --
    // every other deposit's slot, and this deposit's own write-off slot,
    // are untouched.
    depositRefundAttemptsRef.current.set(depositIdNum, decision.attempt);
    depositRefundInFlightRef.current = true;

    const basePayload: any = {
      bank_account_id: Number(refundBank.id),
      amount: amt,
    };
    const payload: any = {
      ...basePayload,
      idempotency_key: decision.idempotencyKey,
    };
    if (!backendRejectsRefundDate) payload.date = refundDate;

    setRefunding(true);
    try {
      await apiPost(`/deposits/${deposit_id}/refund`, payload);

      // Confirmed success (create or replay -- both return the same
      // response shape): clear ONLY this deposit's refund slot back to
      // idle so its next refund (e.g. a later distinct partial refund)
      // gets a fresh key instead of leaking this one into it. Every other
      // deposit's slot, and this deposit's own write-off slot, are
      // untouched. Deliberately does not read any field from the POST
      // response body -- remaining_amount/status are mutable parent
      // state, not owned by this refund event (see design-resolution
      // report); refresh() below reloads the current Deposit instead.
      depositRefundAttemptsRef.current.set(depositIdNum, resolveDepositRefundSuccess());

      setRefundAmt("");
      setRefundDate(ymdTodayLocal());
      await refresh();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const msg =
        typeof detail === "string" ? detail : JSON.stringify(detail || "");

      const failureClass = classifyDepositRefundFailure(e, isClosedPeriodError);

      if (failureClass === "closed-period") {
        // Definite pre-commit rejection: assert_year_open raises before any
        // claim/business write commits, so nothing was persisted. Clear
        // this deposit's refund slot so a corrected date can retry under a
        // new key.
        depositRefundAttemptsRef.current.set(depositIdNum, resolveDepositRefundDefiniteRejection());
        setRefundError(localMessage("sales.errors.closedSubmit", closedThroughDate));
        return;
      }

      if (failureClass === "idempotency-conflict") {
        // Definite: this key is permanently bound to a different,
        // already-completed request. Retrying under it can never succeed
        // for this payload -- clear this deposit's refund slot so the next
        // attempt mints a fresh one. Mirrors the exact Deposit write-off
        // precedent (classifyDepositWriteoffFailure's identical
        // idempotency-conflict branch in doApply): an idempotency conflict
        // is deterministic, not ambiguous, because the CURRENT request's
        // own data was never persisted under this key -- only a prior,
        // different request was. Retaining the ambiguous state here would
        // incorrectly suggest THIS attempt might have committed, which it
        // did not.
        depositRefundAttemptsRef.current.set(depositIdNum, resolveDepositRefundDefiniteRejection());
        setRefundError(localMessage("depositDetail.refundConflict"));
        return;
      }

      if (failureClass === "definite-rejection") {
        depositRefundAttemptsRef.current.set(depositIdNum, resolveDepositRefundDefiniteRejection());

        // Pre-existing legacy fallback, unrelated to idempotency and left
        // untouched here: some historical backend version 422'd on an
        // explicit `date` field with "Input should be None".
        // schemas.DepositRefundCreate.date is now a REQUIRED field (see
        // the idempotency patch), so this fallback's own retry below
        // (which omits date entirely) would itself now 422 if ever
        // reached -- this branch was already unreachable in practice
        // before this patch (confirmed: the backend has long accepted an
        // explicit date fine) and remains so; not something this patch
        // removes or otherwise changes.
        if (
          e?.response?.status === 422 &&
          !backendRejectsRefundDate &&
          msg.includes("Input should be None")
        ) {
          setBackendRejectsRefundDate(true);
          try {
            await apiPost(`/deposits/${deposit_id}/refund`, basePayload);
            setRefundAmt("0.00");
            setRefundDate(ymdTodayLocal());
            await refresh();
            return;
          } catch (e2: any) {
            setRefundError(
              niceDetail(e2?.response?.data?.detail) ||
                errorMessage(e2) ||
                localMessage("depositDetail.refundError"),
            );
            return;
          }
        }

        setRefundError(
          niceDetail(detail) || errorMessage(e) || localMessage("depositDetail.refundError"),
        );
        return;
      }

      // Ambiguous (network failure, timeout, unreadable response, HTTP
      // 500, or anything else unrecognized): retain the key, fingerprint,
      // and submitted payload under THIS deposit's own refund slot so an
      // unchanged retry reuses the same key and safely replays instead of
      // risking a duplicate; an edited retry of THIS SAME deposit stays
      // blocked above. Every other deposit's own slot, and this deposit's
      // own write-off slot, are completely unaffected and remain
      // immediately usable.
      depositRefundAttemptsRef.current.set(
        depositIdNum,
        resolveDepositRefundAmbiguousFailure(decision.attempt),
      );
      setRefundError(
        niceDetail(detail) || errorMessage(e) || localMessage("depositDetail.refundError"),
      );
    } finally {
      depositRefundInFlightRef.current = false;
      setRefunding(false);
    }
  };

  const handleSaveMemo = async () => {
    if (!company_id || !deposit_id) return;
    setMemoError(null);
    setMemoSaving(true);
    try {
      await apiPost(`/deposits/${deposit_id}/memo`, {
        memo: memoDraft.trim() || null,
        company_id, // keep: this endpoint often expects company_id
      });
      await refresh();
      setEditingMemo(false);
    } catch (e: any) {
      setMemoError(
        niceDetail(e?.response?.data?.detail) ||
          errorMessage(e) ||
          localMessage("depositDetail.memoError"),
      );
    } finally {
      setMemoSaving(false);
    }
  };

  const openDelete = () => {
    setDeleteError(null);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    setDeleteError(null);
    if (!deposit) return;

    setDeleting(true);
    try {
      await apiDelete(`/deposits/${deposit.id}`);
      navigate(companyRoute(company_id!, "/recognition/deposits"));
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const msg = String(detail || "");
      if (isClosedPeriodError(e)) {
        setDeleteError(localMessage("sales.errors.closedEdit"));
        return;
      }
      if (
        e?.response?.status === 422 &&
        company_id &&
        msg.toLowerCase().includes("company")
      ) {
        try {
          await apiDelete(`/deposits/${deposit.id}?company_id=${company_id}`);
          navigate(companyRoute(company_id!, "/recognition/deposits"));
          return;
        } catch (e2: any) {
          if (isClosedPeriodError(e2)) {
            setDeleteError(localMessage("sales.errors.closedEdit"));
            return;
          }

          setDeleteError(
            niceDetail(e2?.response?.data?.detail) ||
              errorMessage(e2) ||
              localMessage("depositDetail.voidError"),
          );
          return;
        }
      }

      setDeleteError(
        niceDetail(detail) || errorMessage(e) || localMessage("depositDetail.voidError"),
      );
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen bg-gray-50 flex items-center justify-center text-gray-500 ${bodyCls}`}
      >
        {t("purchases.edit.loading")}
      </div>
    );
  }

  if (err) {
    return (
      <div
        className={`min-h-screen bg-gray-50 flex items-center justify-center text-red-500 ${bodyCls} px-4`}
      >
        {renderMessage(err)}
      </div>
    );
  }

  if (!deposit) {
    return (
      <div
        className={`min-h-screen bg-gray-50 flex items-center justify-center text-gray-500 ${bodyCls} px-4`}
      >
        {t("depositDetail.notFound")}
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-gray-50 flex flex-col items-center pt-6 ${bodyCls}`}
    >
      {/* Delete Modal (SaleDetail-style) */}
      {showDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4"
          onClick={() => {
            if (deleting) return;
            setShowDelete(false);
            setDeleteError(null);
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-sm bg-white border border-gray-200 rounded-2xl shadow-md p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[15px] font-semibold text-gray-700">
              {t("depositDetail.voidTitle")}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              <Trans t={t} i18nKey="depositDetail.voidBody"
                components={{ emphasis: <span className="font-medium" /> }} />
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <Button
                variant="secondary"
                type="button"
                className={btnSmSecondary}
                onClick={() => {
                  if (deleting) return;
                  setShowDelete(false);
                  setDeleteError(null);
                }}
                disabled={deleting}
              >
                {t("directory.common.cancel")}
              </Button>

              <Button
                variant="primary"
                type="button"
                className={btnSmDanger}
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? t("depositDetail.voiding") : t("purchases.detail.void")}
              </Button>
            </div>
            {deleteError && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-center text-[13px] text-red-700 whitespace-pre-wrap">
                {renderMessage(deleteError)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header (sticky + px-2/mb-2 tokens) */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-6xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <div className="flex-none">
          <button
            type="button"
            onClick={goBack}
            className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD z-10`}
            aria-label={t("directory.common.back")}
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </button>
        </div>

        <div className="flex-1 flex justify-center pointer-events-none relative">
          <h2 className="text-[15px] font-semibold tracking-tight text-gray-700 text-center">
            {t("depositDetail.title")}
          </h2>
        </div>

        <div className="flex-none ml-auto">
          <button
            type="button"
            onClick={goHome}
            className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD z-10`}
            aria-label={t("directory.common.close")}
          >
            <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </button>
        </div>
      </div>

      {/* Card */}
      <div className={`${cardCls} w-full max-w-3xl mx-auto px-6 py-5`}>
        <PurchaseInfoHeader
          purchaseLabels={headerLabels}
          date={deposit.date}
          vendor={deposit.party_name || undefined}
          email={undefined}
          statusBubbles={statusBubbles}
          outstanding={!isClosed && remaining > 0 ? remaining : 0}
          dueDate={undefined}
          total={original}
          isPaid={isClosed}
          edited={false}
          lastEditedAt={undefined}
        />

        {/* Summary (Desktop) */}
        <div className="hidden sm:block mb-5">
          <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <table className={tableCls}>
              <colgroup>
                <col style={{ width: "40%" }} />
                <col style={{ width: "40%" }} />
                <col style={{ width: "20%" }} />
              </colgroup>

              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">
                    {identityLabel}
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">
                    {t("bankAccounts.account")}
                  </th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 tabular-nums">
                    {t("sales.common.amount")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {lines.map((line) => (
                  <tr key={line.id} className="border-t border-gray-100">
                    <td className="px-3 py-2">{line.party || "—"}</td>
                    <td className="px-3 py-2">{line.account || "—"}</td>
                    <td className="px-3 py-2 text-right font-medium whitespace-nowrap tabular-nums">
                      {money(line.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary (Mobile) */}
        <div className="block sm:hidden space-y-3 mb-5">
          {lines.map((line) => (
            <div
              key={line.id}
              className="rounded-2xl border border-gray-200 shadow-sm bg-white px-4 py-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-gray-500 mb-1">
                    {identityLabel}
                  </div>
                  <div className="break-words">{line.party || "—"}</div>
                </div>

                <div className="text-right tabular-nums font-medium whitespace-nowrap">
                  {money(line.amount)}
                </div>
              </div>

              <div className="mt-2">
                <div className="text-xs text-gray-500 mb-1">
                  {t("bankAccounts.account")}
                </div>
                <div className="break-words">{line.account || "—"}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Deposit Summary */}
        <div className="mb-6 rounded-2xl border border-gray-200 bg-white px-4 py-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-right tabular-nums">
            <div>
              <div className="text-xs text-gray-500">{t("spendingDeposits.original")}</div>
              <div className="text-[13px] font-medium text-gray-700">
                {money(original)}
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-500">{t("depositDetail.appliedWrittenOff")}</div>
              <div className="text-[13px] text-gray-700">
                {money(usedAmount)}
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-500">{t("depositDetail.refunded")}</div>
              <div className="text-[13px] text-gray-700">
                {money(refundedAmount)}
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-500">{t("spendingDeposits.remaining")}</div>
              <div className="text-[13px] font-medium text-gray-700">
                {money(remaining)}
              </div>
            </div>
          </div>
        </div>

        {/* History */}
        {showHistorySection && (
          <div className="mb-6">
            <div className="text-xs text-gray-500 mb-2">
              {t("depositDetail.history")}
            </div>

            {eventsLoading ? (
              <div className="text-xs text-gray-500">
                {t("depositDetail.loadingHistory")}
              </div>
            ) : eventsError ? (
              <div className="text-[13px] text-red-500">{renderMessage(eventsError)}</div>
            ) : historyRows.length === 0 ? null : (
              <>
                {/* Desktop */}
                <div className="hidden sm:block">
                  <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                    <table className={tableCls}>
                      <colgroup>
                        <col style={{ width: "22%" }} />
                        <col style={{ width: "18%" }} />
                        <col style={{ width: "40%" }} />
                        <col style={{ width: "20%" }} />
                      </colgroup>

                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">
                            {t("sales.common.date")}
                          </th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">
                            {t("depositDetail.action")}
                          </th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">
                            {t("depositDetail.accountTarget")}
                          </th>
                          <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 tabular-nums">
                            {t("sales.common.amount")}
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {historyRows.map((r) => (
                          <tr key={r.id} className="border-t border-gray-100">
                            <td className="px-3 py-2 whitespace-nowrap">
                              {r.date}
                            </td>
                            <td className="px-3 py-2 whitespace-normal break-words">
                              {r.action}
                            </td>
                            <td className="px-3 py-2">
                              <div
                                className="whitespace-normal break-words"
                                style={
                                  {
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  } as any
                                }
                              >
                                {r.displayAccountOrTarget}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right font-medium tabular-nums whitespace-nowrap">
                              {money(r.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile */}
                <div className="block sm:hidden space-y-2">
                  {historyRows.map((r) => (
                    <div
                      key={r.id}
                      className="rounded-2xl border border-gray-200 shadow-sm bg-white px-4 py-3"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                          <div className="text-xs text-gray-500 mb-0.5">
                            {t("sales.common.date")}
                          </div>
                          <div>{r.date}</div>
                        </div>

                        <div className="text-right tabular-nums">
                          <div className="text-xs text-gray-500 mb-0.5">
                            {t("sales.common.amount")}
                          </div>
                          <div className="font-medium">{money(r.amount)}</div>
                        </div>
                      </div>

                      <div className="mb-2">
                        <div className="text-xs text-gray-500 mb-0.5">
                          {t("depositDetail.action")}
                        </div>
                        <div>{r.action}</div>
                      </div>

                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">
                          {t("depositDetail.accountTarget")}
                        </div>
                        <div className="break-words">{r.displayAccountOrTarget}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Actions (Apply / Refund) */}
        {canAct && (
          <>
            {/* segmented control (Apple-ish) */}
            <div className="flex justify-center mb-3">
              <div className="inline-flex rounded-full bg-gray-100 p-1 ring-1 ring-gray-200">
                <button
                  type="button"
                  onClick={() => setActionTab("apply")}
                  className={[
                    "px-4 h-9 rounded-full text-[13px] font-medium leading-none transition",
                    actionTab === "apply"
                      ? "bg-white text-gray-700 shadow-sm"
                      : "text-gray-500 hover:text-gray-700",
                  ].join(" ")}
                >
                  {t("depositDetail.expenseTab")}
                </button>
                <button
                  type="button"
                  onClick={() => setActionTab("refund")}
                  className={[
                    "px-4 h-9 rounded-full text-[13px] font-medium leading-none transition",
                    actionTab === "refund"
                      ? "bg-white text-gray-700 shadow-sm"
                      : "text-gray-500 hover:text-gray-700",
                  ].join(" ")}
                >
                  {t("depositDetail.refund")}
                </button>
              </div>
            </div>

            {actionTab === "apply" ? (
              <div className="bg-gray-50 rounded-2xl px-4 py-3 ring-1 ring-black/5 mb-6">
                <div className="text-xs text-gray-500 mb-2">
                  {t("depositDetail.writeoffTitle")}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      {t("sales.common.date")}
                    </label>
                    <AppleDateInput
                      valueYmd={applyDate}
                      onChangeYmd={setApplyDate}
                      disabled={backendRejectsApplyDate}
                      openCalendarLabel={t("sales.shared.openCalendar")}
                      ariaLabel={t("depositDetail.writeoffDateAria")}
                      inputClassName={`${inputCls} tabular-nums appearance-none disabled:opacity-60`}
                      placeholder="MM/DD/YYYY"
                    />
                    {isClosedApplyDate && (
                      <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        {t("sales.errors.closedWarning")}
                      </div>
                    )}
                    {backendRejectsApplyDate ? (
                      <div className="text-xs text-gray-500 mt-1">
                        {t("depositDetail.legacyDateRejected")}
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      {t("depositDetail.expenseAccount")}
                    </label>
                    <AppleComboBox<Account>
                      preserveQueryOnDisplayChange
                      emptyText={t("sales.shared.noResults")}
                      toggleOptionsLabel={t("sales.shared.toggleOptions")}
                      options={expenseAccounts}
                      value={applyAcct}
                      onChange={setApplyAcct}
                      getOptionSearchText={(a) => a.name}
                      getOptionLabel={(a) => localizedAccountDisplayName(uniqueDisplayAccount(accounts, a.id, a.name), t, a.name)}
                      placeholder={
                        expenseAccounts.length
                          ? t("depositDetail.selectExpenseAccount")
                          : t("depositDetail.noExpenseAccounts")
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      {t("sales.common.amount")}
                    </label>
                    <AppleNumberInput
                      className={`${inputCls} tabular-nums text-right`}
                      value={applyAmt}
                      onChange={(e) => setApplyAmt(e.target.value)}
                      onBlur={() => onMoneyBlur(setApplyAmt, applyAmt)}
                      placeholder="0.00"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="flex justify-end mt-4">
                  <Button
                    onClick={doApply}
                    disabled={
                      applying ||
                      !applyAcct ||
                      !applyAmt ||
                      Number(
                        String(applyAmt || "")
                          .replace(/,/g, "")
                          .trim(),
                      ) <= 0 ||
                      Number(
                        String(applyAmt || "")
                          .replace(/,/g, "")
                          .trim(),
                      ) > remaining ||
                      (!backendRejectsApplyDate && !applyDate)
                    }
                    variant="primary"
                    className={btnSmPrimary}
                  >
                    {applying ? t("directory.common.savingDots") : t("depositDetail.writeoff")}
                  </Button>
                </div>

                <div className="min-h-[18px] mt-2">
                  {applyError ? (
                    <div className="text-xs text-red-500 font-medium">
                      {renderMessage(applyError)}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-2xl px-4 py-3 ring-1 ring-black/5 mb-6">
                <div className="text-xs text-gray-500 mb-2">
                  {t("depositDetail.refundTitle")}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      {t("sales.common.date")}
                    </label>
                    <AppleDateInput
                      valueYmd={refundDate}
                      onChangeYmd={setRefundDate}
                      disabled={backendRejectsRefundDate}
                      openCalendarLabel={t("sales.shared.openCalendar")}
                      ariaLabel={t("depositDetail.refundDateAria")}
                      inputClassName={`${inputCls} tabular-nums appearance-none disabled:opacity-60`}
                      placeholder="MM/DD/YYYY"
                    />
                    {isClosedRefundDate && (
                      <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        {t("sales.errors.closedWarning")}
                      </div>
                    )}
                    {backendRejectsRefundDate ? (
                      <div className="text-xs text-gray-500 mt-1">
                        {t("depositDetail.legacyDateRejected")}
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      {t("depositDetail.refundAccount")}
                    </label>
                    <AppleComboBox<Account>
                      preserveQueryOnDisplayChange
                      emptyText={t("sales.shared.noResults")}
                      toggleOptionsLabel={t("sales.shared.toggleOptions")}
                      options={bankAccounts}
                      value={refundBank}
                      onChange={setRefundBank}
                      getOptionLabel={displayAccount}
                      getOptionSearchText={accountDisplayName}
                      placeholder={
                        bankAccounts.length
                          ? t("depositDetail.selectRefundAccount")
                          : t("depositDetail.noRefundAccounts")
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      {t("sales.common.amount")}
                    </label>
                    <AppleNumberInput
                      className={`${inputCls} tabular-nums text-right`}
                      value={refundAmt}
                      onChange={(e) => setRefundAmt(e.target.value)}
                      onBlur={() => onMoneyBlur(setRefundAmt, refundAmt)}
                      placeholder="0.00"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="flex justify-end mt-4">
                  <Button
                    onClick={doRefund}
                    disabled={
                      refunding ||
                      !refundBank ||
                      !refundAmt ||
                      Number(
                        String(refundAmt || "")
                          .replace(/,/g, "")
                          .trim(),
                      ) <= 0 ||
                      Number(
                        String(refundAmt || "")
                          .replace(/,/g, "")
                          .trim(),
                      ) > remaining ||
                      (!backendRejectsRefundDate && !refundDate)
                    }
                    variant="primary"
                    className={btnSmPrimary}
                  >
                    {refunding ? t("directory.common.savingDots") : t("depositDetail.refund")}
                  </Button>
                </div>

                <div className="min-h-[18px] mt-2">
                  {refundError ? (
                    <div className="text-xs text-red-500 font-medium">
                      {renderMessage(refundError)}
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </>
        )}

        {/* Memo */}
        {!editingMemo ? (
          <MemoSection
            mode="view"
            variant="card"
            value={deposit.memo || ""}
            label={t("sales.common.memo")}
            editAriaLabel={t("sales.common.editMemo")}
            emptyText={t("sales.detail.noMemo")}
            className="mb-6"
            onEditClick={() => {
              setEditingMemo(true);
              setMemoDraft(deposit.memo || "");
              setMemoError(null);
            }}
          />
        ) : (
          <div className="mb-6 rounded-2xl border border-gray-200 bg-white shadow-sm p-4">
            <MemoSection
              mode="edit"
              variant="plain"
              label={t("sales.common.memo")}
              value={memoDraft}
              onChange={setMemoDraft}
              rows={2}
              disabled={memoSaving}
              placeholder={t("sales.common.memoPlaceholder")}
            />

            <div className="flex gap-3 justify-end mt-3">
              <Button
                variant="secondary"
                type="button"
                className={btnSmSecondary}
                onClick={() => {
                  setEditingMemo(false);
                  setMemoDraft(deposit.memo || "");
                  setMemoError(null);
                }}
                disabled={memoSaving}
              >
                {t("directory.common.cancel")}
              </Button>

              <Button
                variant="primary"
                type="button"
                className={btnSmPrimary}
                onClick={handleSaveMemo}
                disabled={memoSaving}
              >
                {memoSaving ? t("directory.common.savingEllipsis") : t("directory.common.save")}
              </Button>
            </div>

            <div className="min-h-[18px] mt-2">
              {memoError ? (
                <div className="text-xs text-red-500 font-medium">
                  {renderMessage(memoError)}
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* Delete */}
        <div className="flex justify-end">
          <Button
            variant="secondary"
            type="button"
            className={btnSmSecondary}
            onClick={openDelete}
            disabled={!deleteAvailable || deleting}
          >
            {t("purchases.detail.void")}
          </Button>
        </div>

        {!deleteAvailable && !deleting ? (
          <div className="text-xs text-gray-400 mt-2 text-right">
            {t("depositDetail.voidAvailability")}
          </div>
        ) : null}
      </div>
    </div>
  );
}
